import qs from 'qs';

import { User } from 'src/components/types';
import { BRANCH_KIND } from 'src/constants/branching-model';
import { preferences as prefs } from 'src/utils/preferences';
import { captureMessageWithTags } from 'src/utils/sentry';
import { atlassianStore as store } from 'src/utils/store';

import {
  AllBranchTypes,
  JIRA_BUG_KEYS,
  EXTENDED_BRANCH_KINDS,
  JIRA_ISSUE_TYPE_MAPPING,
  SAFE_JIRA_ISSUE_TYPES,
} from './constants';
import { JiraIssue } from './types';
import { sanitizeBranchName, sanitizeIssueSummary } from './utils';

const getProjectFromIssueKey = (issueKey: string): string => {
  return issueKey.split('-')[0];
};

const getStorageKey = (issue: JiraIssue): string | null => {
  return (issue.project && `bb-jira-repo-map-${issue.project}`) || null;
};

export const getRepositoryForIssueFromLocalStorage = (
  issue: JiraIssue
): string | null => {
  const storageKey = getStorageKey(issue);
  return (storageKey && store.get(storageKey)) || null;
};

export const setRepositoryForIssueInLocalStorage = (
  issue: JiraIssue,
  repoFullSlug: string
): void => {
  const storageKey = getStorageKey(issue);
  if (storageKey) {
    store.setStrictlyNecessaryItem(storageKey, repoFullSlug);
  }
};

const getRepositoryForJiraIssueUserPrefKey = (issue: JiraIssue | null) => {
  if (!issue?.project) {
    return null;
  }
  return `create-branch:repo-for-jira-project-${issue.project}`;
};

const getRepositoryForIssueFromUserPreferences = async (
  issue: JiraIssue,
  user: User | null | undefined
): Promise<string | null> => {
  const prefsKey = getRepositoryForJiraIssueUserPrefKey(issue);
  if (prefsKey && user?.uuid) {
    try {
      return await prefs.get(user.uuid, prefsKey);
    } catch {
      captureMessageWithTags('Failed to get the preferred repo for issue');
    }
  }
  return null;
};

export const getRepositoryForIssueFromPrefsOrStorage = (
  issue: JiraIssue,
  user: User | null | undefined
): Promise<string | null> => {
  // Read the preferred repository from local storage,
  // falling back to user preferences if not found
  const repositoryFromLocalStorage =
    getRepositoryForIssueFromLocalStorage(issue);
  if (repositoryFromLocalStorage) {
    return Promise.resolve(repositoryFromLocalStorage);
  }
  return getRepositoryForIssueFromUserPreferences(issue, user);
};

export const generateBranchNameFromIssue = (issue: JiraIssue): string => {
  let result = '';
  const summary = sanitizeIssueSummary(
    issue.summary ? issue.summary.toLowerCase() : ''
  );

  if (issue.key && summary) {
    result = `${issue.key}-${summary}`;
  } else if (issue.key) {
    result = issue.key;
  } else if (summary) {
    result = summary;
  }

  result = sanitizeBranchName(result);
  return result.slice(0, 40);
};

export const parseIssueFromQueryString = (query: string): JiraIssue | null => {
  const parsed: { [key: string]: any } = qs.parse(query, {
    ignoreQueryPrefix: true,
  });
  const key = (parsed.issueKey || '').trim();
  const summary = (parsed.issueSummary || '').trim();
  const type = (parsed.issueType || '').trim();

  if (key || summary || type) {
    const issue: JiraIssue = {};
    if (key) {
      issue.key = key;
      issue.project = getProjectFromIssueKey(key);
    }
    if (summary) {
      issue.summary = summary;
    }
    if (type) {
      issue.type = type;
    }
    return issue;
  }
  return null;
};

export const branchTypeFromIssue = (
  issue: JiraIssue,
  isBugfixEnabled: boolean
): AllBranchTypes => {
  if (issue.type) {
    const key = issue.type.toLowerCase();
    if (JIRA_BUG_KEYS.includes(key)) {
      if (isBugfixEnabled) {
        return BRANCH_KIND.BUG_FIX;
      } else {
        return BRANCH_KIND.FEATURE;
      }
    } else {
      // @ts-ignore TODO: fix noImplicitAny error here
      return JIRA_ISSUE_TYPE_MAPPING[key] || EXTENDED_BRANCH_KINDS.OTHER;
    }
  }
  return EXTENDED_BRANCH_KINDS.OTHER;
};

export const getJiraIssueTypeForAnalytics = (issue: JiraIssue) => {
  if (issue.type) {
    const type = issue.type.toLowerCase();
    if (SAFE_JIRA_ISSUE_TYPES.includes(type)) {
      return type;
    }
  }
  return 'custom';
};
